import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Flower2, ChevronRight, ChevronLeft, Calendar, Heart, 
  Sparkles, Target, Moon, Droplets, Shield, BookOpen, Baby
} from 'lucide-react';
import { Button } from '../components/ui/button';

const STEPS = [
  {
    id: 'welcome',
    type: 'info',
    icon: Flower2,
    gradient: 'from-pink-500 via-rose-500 to-purple-600',
    title: 'Bienvenue sur\nFleur de Vie',
    subtitle: 'Votre compagnon de sante feminine personnalise',
    description: 'Repondez a quelques questions pour personnaliser votre experience.',
    buttonText: 'Commencer'
  },
  {
    id: 'goal',
    type: 'choice',
    icon: Target,
    gradient: 'from-purple-600 via-violet-600 to-indigo-600',
    title: 'Quel est votre objectif ?',
    subtitle: 'Nous adapterons votre experience',
    options: [
      { value: 'track_cycle', label: 'Suivre mon cycle', icon: Calendar, desc: 'Predictions de regles et ovulation' },
      { value: 'learn', label: 'Apprendre', icon: BookOpen, desc: 'Education en sante feminine' },
      { value: 'contraception', label: 'Gerer ma contraception', icon: Shield, desc: 'Rappels et suivi de pilule' },
      { value: 'pregnancy', label: 'Planifier une grossesse', icon: Baby, desc: 'Suivi de fertilite' },
      { value: 'wellness', label: 'Bien-etre general', icon: Sparkles, desc: 'Sante globale et astuces' }
    ],
    multi: false
  },
  {
    id: 'last_period',
    type: 'date',
    icon: Droplets,
    gradient: 'from-red-500 via-rose-500 to-pink-600',
    title: 'Quand ont commence\nvos dernieres regles ?',
    subtitle: 'Cela nous aide a predire votre prochain cycle',
    skipLabel: 'Je ne me souviens pas'
  },
  {
    id: 'cycle_length',
    type: 'slider',
    icon: Calendar,
    gradient: 'from-cyan-500 via-blue-500 to-indigo-600',
    title: 'Quelle est la duree\nde votre cycle ?',
    subtitle: 'Le nombre de jours entre deux debuts de regles',
    min: 20,
    max: 45,
    defaultValue: 28,
    unit: 'jours',
    tip: 'La moyenne est de 28 jours'
  },
  {
    id: 'period_length',
    type: 'slider',
    icon: Droplets,
    gradient: 'from-rose-500 via-pink-500 to-fuchsia-600',
    title: 'Combien de jours\ndurent vos regles ?',
    subtitle: 'La duree moyenne de vos saignements',
    min: 2,
    max: 10,
    defaultValue: 5,
    unit: 'jours',
    tip: 'La moyenne est de 3 a 7 jours'
  },
  {
    id: 'symptoms',
    type: 'choice',
    icon: Heart,
    gradient: 'from-amber-500 via-orange-500 to-red-500',
    title: 'Quels symptomes\nressentez-vous ?',
    subtitle: 'Selectionnez tout ce qui s\'applique',
    options: [
      { value: 'cramps', label: 'Crampes', icon: '😣' },
      { value: 'headache', label: 'Maux de tete', icon: '🤕' },
      { value: 'fatigue', label: 'Fatigue', icon: '😴' },
      { value: 'mood_swings', label: 'Sautes d\'humeur', icon: '😢' },
      { value: 'bloating', label: 'Ballonnements', icon: '🫄' },
      { value: 'acne', label: 'Acne', icon: '😖' },
      { value: 'back_pain', label: 'Mal de dos', icon: '💆' },
      { value: 'none', label: 'Aucun', icon: '😊' }
    ],
    multi: true
  },
  {
    id: 'sleep',
    type: 'choice',
    icon: Moon,
    gradient: 'from-indigo-600 via-purple-600 to-violet-700',
    title: 'Comment dormez-vous\ngeneralement ?',
    subtitle: 'Le sommeil influence votre cycle',
    options: [
      { value: 'well', label: 'Tres bien', desc: '7-9h par nuit' },
      { value: 'ok', label: 'Correctement', desc: '5-7h par nuit' },
      { value: 'poor', label: 'Mal', desc: 'Moins de 5h' },
      { value: 'irregular', label: 'Irregulier', desc: 'Ca depend des jours' }
    ],
    multi: false
  },
  {
    id: 'ready',
    type: 'info',
    icon: Sparkles,
    gradient: 'from-emerald-500 via-teal-500 to-cyan-600',
    title: 'Tout est pret !',
    subtitle: 'Votre profil personnalise vous attend',
    description: 'Creez votre compte pour acceder a toutes les fonctionnalites.',
    buttonText: 'Creer mon compte'
  }
];

const Onboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [animating, setAnimating] = useState(false);
  const [slideDir, setSlideDir] = useState('right');

  const current = STEPS[step];
  const progress = ((step) / (STEPS.length - 1)) * 100;

  const goNext = () => {
    if (step >= STEPS.length - 1) {
      localStorage.setItem('onboarding_data', JSON.stringify(answers));
      navigate('/register');
      return;
    }
    setSlideDir('right');
    setAnimating(true);
    setTimeout(() => {
      setStep(s => s + 1);
      setAnimating(false);
    }, 300);
  };

  const goBack = () => {
    if (step <= 0) return;
    setSlideDir('left');
    setAnimating(true);
    setTimeout(() => {
      setStep(s => s - 1);
      setAnimating(false);
    }, 300);
  };

  const selectOption = (stepId, value, multi) => {
    if (multi) {
      const current = answers[stepId] || [];
      if (value === 'none') {
        setAnswers(prev => ({ ...prev, [stepId]: ['none'] }));
      } else {
        const filtered = current.filter(v => v !== 'none');
        setAnswers(prev => ({
          ...prev,
          [stepId]: filtered.includes(value) ? filtered.filter(v => v !== value) : [...filtered, value]
        }));
      }
    } else {
      setAnswers(prev => ({ ...prev, [stepId]: value }));
      setTimeout(goNext, 400);
    }
  };

  const canProceed = () => {
    if (current.type === 'info') return true;
    if (current.type === 'choice' && !current.multi) return !!answers[current.id];
    if (current.type === 'choice' && current.multi) return (answers[current.id] || []).length > 0;
    if (current.type === 'date') return true; // skip allowed
    if (current.type === 'slider') return true;
    return true;
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${current.gradient} flex flex-col transition-all duration-500`} data-testid="onboarding">
      {/* Progress Bar */}
      {step > 0 && (
        <div className="absolute top-0 left-0 right-0 z-50 px-6 pt-4">
          <div className="flex items-center gap-3">
            <button onClick={goBack} className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors" data-testid="onboarding-back">
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <div className="flex-1 h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-white rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
            <span className="text-white/60 text-xs font-medium">{step}/{STEPS.length - 1}</span>
          </div>
        </div>
      )}

      {/* Content */}
      <div className={`flex-1 flex flex-col items-center justify-center px-6 py-16 transition-all duration-300 ${animating ? (slideDir === 'right' ? 'opacity-0 translate-x-8' : 'opacity-0 -translate-x-8') : 'opacity-100 translate-x-0'}`}>
        
        {/* Icon */}
        <div className="w-20 h-20 rounded-3xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-8">
          <current.icon className="w-10 h-10 text-white" />
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-bold text-white text-center mb-3 whitespace-pre-line leading-tight">
          {current.title}
        </h1>
        <p className="text-white/70 text-center text-base mb-8 max-w-sm">{current.subtitle}</p>

        {/* === INFO TYPE === */}
        {current.type === 'info' && (
          <div className="w-full max-w-sm text-center">
            {current.description && <p className="text-white/60 text-sm mb-10">{current.description}</p>}
            <Button onClick={goNext} className="w-full h-14 bg-white text-gray-900 hover:bg-white/90 text-lg font-bold rounded-2xl shadow-xl" data-testid="onboarding-cta">
              {current.buttonText} <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            {step === 0 && (
              <button onClick={() => navigate('/login')} className="mt-4 text-white/60 hover:text-white text-sm transition-colors" data-testid="onboarding-login-link">
                J'ai deja un compte
              </button>
            )}
          </div>
        )}

        {/* === CHOICE TYPE === */}
        {current.type === 'choice' && (
          <div className="w-full max-w-sm space-y-3">
            {current.options.map(opt => {
              const selected = current.multi 
                ? (answers[current.id] || []).includes(opt.value)
                : answers[current.id] === opt.value;
              return (
                <button key={opt.value} onClick={() => selectOption(current.id, opt.value, current.multi)}
                  className={`w-full p-4 rounded-2xl flex items-center gap-4 transition-all duration-200 ${
                    selected ? 'bg-white text-gray-900 shadow-xl scale-[1.02]' : 'bg-white/15 text-white hover:bg-white/25'
                  }`} data-testid={`opt-${opt.value}`}>
                  {opt.icon && (typeof opt.icon === 'string' 
                    ? <span className="text-2xl">{opt.icon}</span>
                    : <opt.icon className={`w-6 h-6 ${selected ? 'text-gray-900' : 'text-white'}`} />
                  )}
                  <div className="text-left flex-1">
                    <p className="font-semibold">{opt.label}</p>
                    {opt.desc && <p className={`text-xs ${selected ? 'text-gray-500' : 'text-white/50'}`}>{opt.desc}</p>}
                  </div>
                  {current.multi && (
                    <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center ${selected ? 'bg-gray-900 border-gray-900' : 'border-white/40'}`}>
                      {selected && <span className="text-white text-xs font-bold">✓</span>}
                    </div>
                  )}
                </button>
              );
            })}
            {current.multi && (
              <Button onClick={goNext} disabled={!canProceed()} className="w-full h-12 bg-white text-gray-900 hover:bg-white/90 rounded-2xl font-bold mt-4 disabled:opacity-40" data-testid="onboarding-next">
                Continuer <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            )}
          </div>
        )}

        {/* === DATE TYPE === */}
        {current.type === 'date' && (
          <div className="w-full max-w-sm space-y-4">
            <input type="date" value={answers[current.id] || ''} onChange={(e) => setAnswers(prev => ({ ...prev, [current.id]: e.target.value }))}
              max={new Date().toISOString().split('T')[0]}
              className="w-full h-14 px-6 rounded-2xl bg-white/15 text-white text-lg text-center border-2 border-white/30 focus:border-white focus:outline-none"
              data-testid="onboarding-date" />
            <Button onClick={goNext} disabled={!answers[current.id]} className="w-full h-12 bg-white text-gray-900 hover:bg-white/90 rounded-2xl font-bold disabled:opacity-40" data-testid="onboarding-next">
              Continuer <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
            <button onClick={() => { setAnswers(prev => ({ ...prev, [current.id]: null })); goNext(); }}
              className="w-full text-white/50 hover:text-white text-sm transition-colors" data-testid="onboarding-skip">
              {current.skipLabel}
            </button>
          </div>
        )}

        {/* === SLIDER TYPE === */}
        {current.type === 'slider' && (
          <div className="w-full max-w-sm space-y-6">
            <div className="text-center">
              <span className="text-6xl font-bold text-white">{answers[current.id] || current.defaultValue}</span>
              <span className="text-white/60 text-xl ml-2">{current.unit}</span>
            </div>
            <input type="range" min={current.min} max={current.max} value={answers[current.id] || current.defaultValue}
              onChange={(e) => setAnswers(prev => ({ ...prev, [current.id]: parseInt(e.target.value) }))}
              className="w-full h-3 appearance-none rounded-full bg-white/20 cursor-pointer accent-white"
              style={{ accentColor: 'white' }}
              data-testid="onboarding-slider" />
            <div className="flex justify-between text-white/40 text-xs">
              <span>{current.min} {current.unit}</span>
              <span>{current.tip}</span>
              <span>{current.max} {current.unit}</span>
            </div>
            <Button onClick={goNext} className="w-full h-12 bg-white text-gray-900 hover:bg-white/90 rounded-2xl font-bold" data-testid="onboarding-next">
              Continuer <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
